import type { BinaryImage } from './threshold.js';
import type { DetectedCircle } from './detector.js';

/**
 * Circles, by fitting connected components — and deliberately not by a Hough
 * transform.
 *
 * ADR 0009 measured `HoughCircles` on this corpus: recall 1.000 at these radii,
 * buried in 242 to 2010 false positives, so "the recall is free and the precision
 * is the work". A vote-counting transform is the wrong instrument for that work,
 * because every accumulator peak is a *hypothesis about the whole plane* and the
 * ink that voted for it is thrown away. Labelling the ink first keeps it: each
 * component is one stroke, and a stroke either is a circle or is not.
 *
 * **Two criteria, and neither one alone is enough. That is the finding.** A
 * component is kept only if it is BOTH round — its ink sits at a constant distance
 * from the fitted centre — AND complete — that ink surrounds the centre. Measured
 * on RF09.300.clean (2481x3508, 4.1667 px/pt, 256 components), in page points:
 *
 *     component            r (pt)   radial RMS   RMS/r    coverage
 *     the four circles     16-22       0.205     0.0128    1.000   kept
 *     three separate ARCs  19.94       0.206     0.0104    0.375   round, not complete
 *     the merged arc blob  12.30       4.425     0.360     0.984   complete, not round
 *     the drawing frame   368.52      60.354     0.164     1.000   complete, not round
 *     five zigzag polys    43.35      22.379     0.516     0.438   neither
 *     a straight rule     138.65      68.195     0.492     0.500   neither
 *
 * Read the two middle rows together. **The ARCs are as round as a real circle** —
 * 0.206 pt of radial RMS against the circles' 0.205 — because a 130 degree arc of a
 * circle is a circle everywhere it exists, so a residual test alone keeps every one
 * of the corpus's 78 separately-labelled arcs. **The merged blob, where the fourth
 * arc touches its neighbour, is as complete as a real circle** — 0.984 here and a
 * full 1.000 in sixteen of the eighteen images where it forms — and so is the
 * drawing frame, so a coverage test alone keeps those. Only the conjunction
 * separates them, and it
 * separates them across the whole corpus: of 5987 fitted components in 24 images,
 * exactly the 96 true circles pass both and nothing else does.
 *
 * Scored by the acceptance runner's own scorer and tolerance, that is TP 96, FP 0,
 * FN 0 — CIRCLE F1 1.000 on every one of the 24 images, clean and degraded, at 300,
 * 450 and 600 DPI. The worst geometry error over all 96 matches is 0.185 pt, on the
 * 300 DPI sheets, where the tolerance is 0.691. Measured by a probe and not by the
 * gate: this module is not yet wired into `baselineDetector`, so the figure ADR 0009
 * publishes for circles is still 0.000 and stays 0.000 until it is.
 *
 * Roundness is expressed as RMS **relative to the fitted radius**, so that it is a
 * property of the shape and not of the scan. Absolute RMS moves with resolution and
 * degradation — clean 0.204/0.190/0.183 pt at 300/450/600 DPI, noisy 0.274/0.227/
 * 0.207 — while the ratio stays inside 0.0083 to 0.0171 for all 96 true circles at
 * all six configurations.
 *
 * Cost and memory, both measured, because this ships to a browser and the largest
 * corpus image is RF05.600.noisy at 6751x9718 = 65.6 megapixels. The moments a
 * circle fit needs are accumulated during the flood fill itself, so labelling and
 * fitting are one pass; a second pass over the image measures residual and
 * coverage, dispatching each ink pixel by its label. No array of pixel indices is
 * ever built per component, and the flood-fill stack grows by doubling from 1024
 * rather than being sized at width*height — which on that image would be a 262 MB
 * allocation to hold, typically, a few hundred pixels.
 *
 * That leaves the label array as the one large allocation, 4 bytes a pixel: 35 MB
 * on a 300 DPI A4 sheet and 262 MB on RF05.600.noisy. It is worth being precise
 * about how big that is rather than defending it in the abstract — the summed-area
 * table `binariseAdaptive` has already built for the same image is a Float64Array
 * of (w+1)(h+1), which is 525 MB, so this stage doubles nothing.
 *
 * End to end, `detectCircles` measured 41-61 ms on the 300 DPI sheets, 138-164 ms
 * at 450 and 600, and 294 ms on RF05.600.noisy. Labelling and fitting is the
 * cheaper half — 16 ms of the 53 on RF09.300.clean, 83 ms of the 294 on the largest
 * sheet — because it visits only ink, where the second pass reads every pixel's
 * label.
 */

/**
 * How many angular sectors the fitted centre is divided into.
 *
 * It has to resolve the gap between a 130 degree arc (0.361 of a turn, which is
 * what the corpus draws) and a closed circle (1.000), and it has to be coarse
 * enough that a real circle still fills every sector. 64 sectors is 5.6 degrees
 * each; the smallest circle in the corpus is r = 16 pt, which at the lowest
 * resolution measured is a 66.7 px radius and a 419 px circumference — 6.5 pixels
 * per sector, so a complete ring cannot leave one empty by rasterisation alone.
 * It does not: every one of the 96 true circles across all 24 images measures
 * coverage exactly 1.000, and the corpus's 130 degree arcs measure 0.375 to 0.391
 * against their ideal 0.361, the excess being the stroke's own width at the two
 * ends. Two 32-bit words hold the whole mask, so the cost is 8 bytes a component.
 */
const ANGULAR_SECTORS = 64;

/**
 * The smallest circle worth reporting, in **page points** rather than pixels.
 *
 * Points because the thing being excluded is physical: a capital O or a zero in
 * 10 pt text is a closed, round ring of radius about 3 pt and passes both criteria
 * on its geometry alone. Pixels would make this constant mean a different drawing
 * at every DPI.
 *
 * **The sweep says it removes nothing this corpus contains, and that is stated
 * rather than dressed up.** Sweeping the floor over all 24 images with the other
 * two criteria in force gives 96 true positives and **zero** false positives at
 * every floor from 0 pt to 16 pt; 17 pt is where it starts costing recall, by
 * dropping the 24 circles of radius 16. The roundness and coverage pair is already
 * enough here — the nearest glyph to passing is a 2.305 pt ring at 600 DPI with a
 * roundness of 0.0884, which fails the 0.05 threshold by 1.8x on its own.
 *
 * So the floor is insurance, and it is placed by the radius histogram rather than
 * by a false-positive count it cannot improve. Fitted radii of all 5987 components
 * the corpus produces, in points:
 *
 *     r < 1.5        310   speckle rings and blur artefacts
 *     1.5 to 4.5    5119   glyph bowls: O, D, 0, 6, 8, 9 and the like, max 4.175
 *     4.5 to 12.2      0   <- the empty band
 *     12.2 to 12.4    18   the merged arc blob, one per image
 *     16.0 to 22.1   174   the 96 true circles and the 78 arcs
 *     above 22.1     366   rules, zigzags, the frame
 *
 * 8 pt is the geometric centre of the band between the largest glyph fitted
 * anywhere (4.175 pt) and the smallest true circle (16.001 pt) — sqrt of their
 * product is 8.17 — which is where a constant belongs when the data leaves a hole
 * that wide and no measurement inside it.
 */
export const MIN_RADIUS_PT = 8;

/**
 * How far the ink may wander from the fitted circle, as a fraction of its radius.
 *
 * Swept over all 24 images, with the 8 pt floor and the coverage test in force:
 *
 *     threshold   0.0100  0.0171  0.0200  0.1500  0.1546  0.2000  0.4000
 *     TP of 96        25      95      96      96      96      96      96
 *     FP               0       0       0       0       2      24      42
 *
 * Full recall from 0.0171 — the worst true circle in the corpus, on the degraded
 * 300 DPI sheet — and zero false positives until 0.1546, where the drawing frame
 * gets in. That is a factor of nine of empty band, and every one of the 42
 * complete-but-not-round components sits above it.
 *
 * 0.05 is the band's geometric centre: 2.9x looser than the worst true circle and
 * 3.1x tighter than the nearest reject. A threshold placed at either end of an
 * empty band is a threshold that will move.
 *
 * **What dividing by the radius costs, since it is not free.** A very gentle curve
 * has a small residual *relative to the huge radius it implies*, so the relative
 * form is exactly the form a straight-ish stroke can pass: on RF02.300.noisy a
 * 7754-pixel stroke fits r = 422 pt at a roundness of 0.0467, inside this threshold.
 * Its angular coverage is 0.188. That is the clearest single case for why the two
 * criteria are held together rather than either being tuned until it is sufficient.
 */
export const MAX_RADIAL_RMS_FRACTION = 0.05;

/**
 * How much of the way round the fitted centre the ink must go.
 *
 * What this separates is the arcs, which are otherwise indistinguishable from a
 * circle. Swept over all 24 images, with the 8 pt floor and the roundness test in
 * force:
 *
 *     threshold   0.35  0.39  0.40  0.50  0.90  1.00
 *     TP of 96      96    96    96    96    96    96
 *     FP            78     5     0     0     0     0
 *
 * The 78 are the corpus's arcs, which measure 0.375 to 0.391, and every true circle
 * measures exactly 1.000 — so anything from 0.40 upward is both correct and free.
 *
 * 0.90 rather than the middle of that band, because the two ends are not
 * symmetric in what they risk. Above, the margin is spent on a defect that happens:
 * a circle broken by a 30 degree gap in a bad scan measures 0.917 and is still
 * found. Below, the margin buys nothing but arcs — and a drawing containing a
 * three-quarter arc, which this corpus does not, would be read as a circle at any
 * threshold under 0.75. 0.90 excludes every arc up to 324 degrees of sweep while
 * leaving a true circle 6 of its 64 sectors to lose.
 */
export const MIN_ANGULAR_COVERAGE = 0.9;

export interface CircleOptions {
  /**
   * The image's scale, used only to read `MIN_RADIUS_PT` in pixels. Defaults to 1,
   * which makes the floor 8 pixels — the honest reading when nothing is known
   * about the page.
   */
  readonly pixelsPerPoint?: number;
  /** Overrides the physical floor outright, in pixels. */
  readonly minRadiusPx?: number;
  readonly maxRadialRmsFraction?: number;
  readonly minAngularCoverage?: number;
}

/**
 * One connected component, fitted and measured.
 *
 * Exposed because the two criteria are only convincing side by side: this is what
 * lets a test assert that the arc passes roundness and the square passes coverage,
 * which is the claim the conjunction rests on, and what lets a probe sweep the
 * thresholds against the corpus instead of guessing them.
 */
export interface CircleCandidate {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  /** Ink pixels in the component. */
  readonly pixels: number;
  /** RMS of `|p - centre| - r` over the component, in pixels. */
  readonly radialRms: number;
  /** Fraction of the sectors around the fitted centre that hold ink, 0 to 1. */
  readonly angularCoverage: number;
}

export function detectCircles(image: BinaryImage, options: CircleOptions = {}): DetectedCircle[] {
  const maxRms = options.maxRadialRmsFraction ?? MAX_RADIAL_RMS_FRACTION;
  const minCoverage = options.minAngularCoverage ?? MIN_ANGULAR_COVERAGE;
  const minRadius = minRadiusPx(options);

  const found: DetectedCircle[] = [];
  for (const c of circleCandidates(image, options)) {
    if (c.r < minRadius) continue;
    const roundness = c.radialRms / c.r;
    if (roundness > maxRms) continue;
    if (c.angularCoverage < minCoverage) continue;
    found.push({
      kind: 'circle',
      cx: c.cx,
      cy: c.cy,
      r: c.r,
      confidence: confidenceOf(c, maxRms),
    });
  }

  // Sorted by where the circle is, so the output order is a property of the image
  // rather than of the raster order that happened to label the components.
  return found.sort((p, q) => p.cy - q.cy || p.cx - q.cx || p.r - q.r);
}

/**
 * Confidence, from the detector's own two measurements and nothing else.
 *
 * The product of how far round the ink goes and how tightly it holds the radius,
 * where tightness is the fraction of the roundness budget left unspent. Both terms
 * are in [0,1] for anything this function is asked about, and the product moves
 * with the evidence in the direction it should: the same four circles on RF09
 * report 0.74-0.81 at 300 DPI clean, 0.77-0.83 at 600 DPI clean, and 0.67-0.76 on
 * the degraded 300 DPI sheet. Sharper scan, higher confidence; noisier scan, lower.
 * Coverage is 1.000 for every circle in the corpus, so on this corpus the figure is
 * carried entirely by tightness — which is worth saying, because it means the
 * coverage term is untested as a *gradation* here and only tested as a gate.
 *
 * A constant here would be a lie with a number attached — see the confidence note
 * in `detect-baseline.ts`, which says the same thing about vote counts.
 */
function confidenceOf(c: CircleCandidate, maxRadialRmsFraction: number): number {
  const tightness = 1 - c.radialRms / (c.r * maxRadialRmsFraction);
  return Math.max(0, Math.min(1, c.angularCoverage * tightness));
}

function minRadiusPx(options: CircleOptions): number {
  return options.minRadiusPx ?? MIN_RADIUS_PT * (options.pixelsPerPoint ?? 1);
}

/**
 * Every component of the ink, fitted and measured — the raw material of the
 * decision, before any of it is applied.
 *
 * Pass one labels the 8-connected components and accumulates, in the same visit,
 * the nine moments the algebraic fit needs. Pass two walks the image again and
 * measures each pixel's residual and its sector, dispatching by label. Components
 * that cannot be a circle at all are dropped between the passes — too few pixels
 * to fill the sectors, a singular fit, a radius past the page diagonal — so pass
 * two touches only ink that is still in the running.
 */
export function circleCandidates(
  image: BinaryImage,
  options: CircleOptions = {},
): CircleCandidate[] {
  const { width, height } = image;
  const minCoverage = options.minAngularCoverage ?? MIN_ANGULAR_COVERAGE;

  const { labels, cx, cy, r, pixels } = labelAndFit(image, minCoverage);
  const count = r.length;
  if (count === 0) return [];

  // Only the survivors get a slot, so the per-component state of pass two is
  // proportional to what is plausible rather than to how much speckle the scanner
  // added: on RF09.300.noisy that is 241 slots out of 9515 components.
  const slotOf = new Int32Array(count).fill(-1);
  const diagonal = Math.hypot(width, height);
  const minPixels = Math.ceil(minCoverage * ANGULAR_SECTORS);
  let slots = 0;
  for (let i = 0; i < count; i += 1) {
    const radius = r[i]!;
    if (!Number.isFinite(radius) || radius <= 0 || radius > diagonal) continue;
    if (pixels[i]! < minPixels) continue;
    slotOf[i] = slots;
    slots += 1;
  }
  if (slots === 0) return [];

  const squaredError = new Float64Array(slots);
  // 64 sectors as two 32-bit words, because JavaScript's bitwise operators are
  // 32-bit and a Uint8Array of flags per sector would cost eight times as much.
  const sectorsLow = new Uint32Array(slots);
  const sectorsHigh = new Uint32Array(slots);

  // The fitted circle of each surviving component, gathered by slot so the inner
  // loop reads three contiguous typed arrays instead of three sparse ones.
  const slotCx = new Float64Array(slots);
  const slotCy = new Float64Array(slots);
  const slotR = new Float64Array(slots);
  for (let i = 0; i < count; i += 1) {
    const slot = slotOf[i]!;
    if (slot < 0) continue;
    slotCx[slot] = cx[i]!;
    slotCy[slot] = cy[i]!;
    slotR[slot] = r[i]!;
  }

  const { ink } = image;
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      if (ink[row + x] === 0) continue;
      const slot = slotOf[labels[row + x]!]!;
      if (slot < 0) continue;
      const dx = x - slotCx[slot]!;
      const dy = y - slotCy[slot]!;
      // sqrt of the sum rather than Math.hypot: hypot guards against an overflow
      // that cannot happen inside an image, and costs several times as much.
      const off = Math.sqrt(dx * dx + dy * dy) - slotR[slot]!;
      squaredError[slot] = squaredError[slot]! + off * off;
      // atan2 lands in (-pi, pi]; shifting by pi puts sector 0 at due west and
      // makes the mapping monotone, which is all a coverage count needs.
      const sector = Math.min(
        ANGULAR_SECTORS - 1,
        Math.floor(((Math.atan2(dy, dx) + Math.PI) / (2 * Math.PI)) * ANGULAR_SECTORS),
      );
      if (sector < 32) sectorsLow[slot] = sectorsLow[slot]! | (1 << sector);
      else sectorsHigh[slot] = sectorsHigh[slot]! | (1 << (sector - 32));
    }
  }

  const out: CircleCandidate[] = [];
  for (let i = 0; i < count; i += 1) {
    const slot = slotOf[i]!;
    if (slot < 0) continue;
    const n = pixels[i]!;
    const filled = popcount(sectorsLow[slot]!) + popcount(sectorsHigh[slot]!);
    out.push({
      cx: cx[i]!,
      cy: cy[i]!,
      r: r[i]!,
      pixels: n,
      radialRms: Math.sqrt(squaredError[slot]! / n),
      angularCoverage: filled / ANGULAR_SECTORS,
    });
  }
  return out;
}

interface LabelledFits {
  readonly labels: Int32Array;
  readonly cx: readonly number[];
  readonly cy: readonly number[];
  readonly r: readonly number[];
  readonly pixels: readonly number[];
}

/**
 * Label the ink and fit a circle to each component, in one visit per pixel.
 *
 * The moments are accumulated **relative to the component's seed pixel**, and that
 * is a precision decision rather than a convenience. Fitting a circle needs third
 * moments; taken about the image origin, a component near the far corner of a 600
 * DPI sheet sums terms of order x^3 = 1e12 over 1e6 pixels, which is 1e18 and past
 * the 2^53 where an integer stops being exact in a double. Taken about the seed the
 * offsets never exceed the component's own extent, so for anything circle-sized
 * every moment is an exactly represented integer and the fit has no error but the
 * ink's own. The centre is shifted back at the end, which is exact.
 */
function labelAndFit(image: BinaryImage, minCoverage: number): LabelledFits {
  const { width, height, ink } = image;
  const labels = new Int32Array(width * height).fill(-1);
  const cx: number[] = [];
  const cy: number[] = [];
  const r: number[] = [];
  const pixels: number[] = [];

  // Grown by doubling. Sizing it at width*height would be a 262 MB allocation on
  // the corpus's largest sheet to hold, typically, a few hundred pixels.
  let stack = new Int32Array(1024);
  const minPixels = Math.ceil(minCoverage * ANGULAR_SECTORS);

  for (let seed = 0; seed < labels.length; seed += 1) {
    if (ink[seed] === 0 || labels[seed] !== -1) continue;
    const id = r.length;
    const seedX = seed % width;
    const seedY = (seed / width) | 0;

    let n = 0;
    let sx = 0;
    let sy = 0;
    let sxx = 0;
    let sxy = 0;
    let syy = 0;
    let sz = 0;
    let sxz = 0;
    let syz = 0;

    labels[seed] = id;
    stack[0] = seed;
    let top = 1;

    while (top > 0) {
      top -= 1;
      const p = stack[top]!;
      const x = p % width;
      const y = (p / width) | 0;
      const dx = x - seedX;
      const dy = y - seedY;
      const z = dx * dx + dy * dy;
      n += 1;
      sx += dx;
      sy += dy;
      sxx += dx * dx;
      sxy += dx * dy;
      syy += dy * dy;
      sz += z;
      sxz += dx * z;
      syz += dy * z;

      if (top + 8 > stack.length) {
        const bigger = new Int32Array(stack.length * 2);
        bigger.set(stack);
        stack = bigger;
      }
      const x0 = x > 0 ? x - 1 : 0;
      const x1 = x + 1 < width ? x + 1 : width - 1;
      const y0 = y > 0 ? y - 1 : 0;
      const y1 = y + 1 < height ? y + 1 : height - 1;
      for (let ny = y0; ny <= y1; ny += 1) {
        const nrow = ny * width;
        for (let nx = x0; nx <= x1; nx += 1) {
          const q = nrow + nx;
          if (ink[q] === 0 || labels[q] !== -1) continue;
          labels[q] = id;
          stack[top] = q;
          top += 1;
        }
      }
    }

    pixels.push(n);
    // A component with fewer ink pixels than there are sectors to fill cannot reach
    // the coverage threshold whatever shape it is, so it is never fitted. That is
    // what makes a degraded sheet cost what a clean one does: RF09.300.noisy labels
    // 9515 components against the clean sheet's 256, and 9274 of them are speckle
    // dropped here, leaving 241 to fit against the clean sheet's 245.
    if (n < minPixels || n < 3) {
      cx.push(NaN);
      cy.push(NaN);
      r.push(NaN);
      continue;
    }
    const fit = solveKasa(n, sx, sy, sxx, sxy, syy, sz, sxz, syz);
    cx.push(seedX + fit.cx);
    cy.push(seedY + fit.cy);
    r.push(fit.r);
  }

  return { labels, cx, cy, r, pixels };
}

/**
 * The algebraic circle fit: minimise the residual of `x^2 + y^2 = A x + B y + C`,
 * which is linear in A, B, C and so closes in one pass of moments.
 *
 * Kasa's fit rather than a geometric (Levenberg-Marquardt) one, and the reason is
 * measured. The known weakness of the algebraic fit is that it pulls the radius
 * inward on a short arc, and the components that survive here cover at least 90% of
 * a turn by construction, so that weakness is out of reach. Against the holdout's
 * truth the fit lands within 0.185 pt of the annotated centre and radius at 300 DPI
 * and 0.086 pt at 600, against a matching tolerance of 0.691 pt — a quarter of the
 * budget at worst — so there is nothing for an iteration to buy.
 *
 * A radius of `NaN` when the normal equations are singular or the implied radius is
 * imaginary, which is what a straight line or a three-pixel speck produces. It is
 * dropped by the finite check in `circleCandidates` rather than thrown, because a
 * sheet of scanned drawing contains thousands of specks and none of them is an
 * error.
 */
function solveKasa(
  n: number,
  sx: number,
  sy: number,
  sxx: number,
  sxy: number,
  syy: number,
  sz: number,
  sxz: number,
  syz: number,
): { cx: number; cy: number; r: number } {
  const mx = sx / n;
  const my = sy / n;
  const mz = sz / n;

  // Centred on the component's own centroid, which removes C from the system and
  // leaves a 2x2 that is well conditioned for any component that is not a line.
  const cxx = sxx - sx * mx;
  const cxy = sxy - sx * my;
  const cyy = syy - sy * my;
  const cxz = sxz - sx * mz;
  const cyz = syz - sy * mz;

  const determinant = cxx * cyy - cxy * cxy;
  if (determinant === 0) return { cx: NaN, cy: NaN, r: NaN };

  const a = (cxz * cyy - cyz * cxy) / determinant;
  const b = (cyz * cxx - cxz * cxy) / determinant;
  const centreX = a / 2;
  const centreY = b / 2;
  const c = mz - a * mx - b * my;
  const rSquared = c + centreX * centreX + centreY * centreY;
  if (!(rSquared > 0)) return { cx: NaN, cy: NaN, r: NaN };
  return { cx: centreX, cy: centreY, r: Math.sqrt(rSquared) };
}

function popcount(v: number): number {
  let x = v - ((v >>> 1) & 0x55555555);
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333);
  x = (x + (x >>> 4)) & 0x0f0f0f0f;
  return (x * 0x01010101) >>> 24;
}
